import { useMemo } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { useCandidAutoTagging } from '@/api/hooks/use-candid-auto-tagging'
import { useCandidDetail } from '@/api/hooks/use-candid-detail'
import { usePhotoCandidFromCache } from '@/api/hooks/use-photo-candid-from-cache'
import { buildCaptionTextLines, hasNamedCaptionTags } from '@/lib/caption/build-caption-paragraphs'
import {
  isPortraitPlacedPhoto,
  resolvePlacedPhotoIdentity,
} from '@/lib/photos/fill-shape-with-photo'
import { resolvePlacedClikArtId } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import {
  CLIKART_ENABLED_PHOTO_ACTION_IDS,
  type PhotoActionId,
} from '@/lib/photos/photo-action-items'
import { useUnitOfMeasure } from '@/contexts/editor-settings-context'
import { formatPointsForDisplay } from '@/lib/units/unit-conversion'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`

  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`
}

function joinName(first?: string, last?: string): string {
  return `${first ?? ''} ${last ?? ''}`.trim()
}

export type PhotoPanelContext = {
  photoIdentity: { imageId: string; displayName: string } | null
  fileNameLabel: string
  hasPortraitMetadata: boolean
  isPortraitSource: boolean
  isCaptionDisabled: boolean
  portraitStudentName: string
  taggedLines: string[]
  dimensionsLabel: string
  fileSize: string
  albumLabel: string
  uploadedByLabel: string
  approvedByLabel: string
  portraitLabel: string
  isActionDisabled: (id: PhotoActionId) => boolean
}

export function usePhotoPanelContext(
  selectedPhotoShape: ShapeData | undefined,
  options?: {
    interactionLocked?: boolean
    isRemovingBackground?: boolean
  }
): PhotoPanelContext {
  const unitOfMeasure = useUnitOfMeasure()
  const photoIdentity = selectedPhotoShape ? resolvePlacedPhotoIdentity(selectedPhotoShape) : null
  const isPortraitSource = selectedPhotoShape ? isPortraitPlacedPhoto(selectedPhotoShape) : false
  const candid = usePhotoCandidFromCache(photoIdentity?.imageId)
  const { data: detail } = useCandidDetail(photoIdentity?.imageId ?? null)
  const { data: autoTaggingData } = useCandidAutoTagging(photoIdentity?.imageId ?? null)

  const hasPortraitMetadata = !!detail?.lastMadePortraitDate
  const isClikArt = selectedPhotoShape ? resolvePlacedClikArtId(selectedPhotoShape) !== null : false
  const interactionLocked = options?.interactionLocked ?? false
  const isRemovingBackground = options?.isRemovingBackground ?? false
  const isCaptionDisabled = !hasPortraitMetadata && !hasNamedCaptionTags(autoTaggingData)

  const portraitStudentName = useMemo(() => {
    const student = candid?.students?.[0]

    if (!student) return '—'

    return `${student.firstName} ${student.lastName}`.trim() || '—'
  }, [candid])

  const taggedLines = useMemo(() => buildCaptionTextLines(autoTaggingData), [autoTaggingData])

  const dimensionsLabel = useMemo(() => {
    if (!selectedPhotoShape) return '—'

    const printW = formatPointsForDisplay(selectedPhotoShape.width, unitOfMeasure)
    const printH = formatPointsForDisplay(selectedPhotoShape.height, unitOfMeasure)

    if (detail) return `${printW} x ${printH} (${detail.pixelWidth} x ${detail.pixelHeight}px)`

    return `${printW} x ${printH}`
  }, [selectedPhotoShape, unitOfMeasure, detail])

  const fileSize = detail ? formatFileSize(detail.fileSize) : '—'
  const fileNameLabel = detail?.displayName || photoIdentity?.displayName || '—'

  const albumLabel = detail?.candidAlbumName || '—'
  const snapDetails = detail?.originType === 'SNAP' ? detail.snapImageDetails : undefined
  // A snap's uploader and approver live on `snapImageDetails`; its top-level `uploadStaff*` fields
  // are not the person who uploaded the snap, so a snap never falls back to them.
  const uploadedByLabel =
    (snapDetails
      ? joinName(snapDetails.uploadedByFirstName, snapDetails.uploadedByLastName)
      : joinName(detail?.uploadStaffFirstName, detail?.uploadStaffLastName)) || '—'
  const approvedByLabel =
    (snapDetails?.approvedFlag === true ? snapDetails.approvedDisplayName?.trim() : '') || '—'
  const portraitLabel = hasPortraitMetadata || isPortraitSource ? 'Yes' : 'No'

  const isActionDisabled = (id: PhotoActionId): boolean => {
    if (interactionLocked) return true
    // A placed ClikArt gets only its own four actions; the toolbar, context menu and left panel all
    // read this, so they can't disagree about what is enabled.
    if (isClikArt) return !CLIKART_ENABLED_PHOTO_ACTION_IDS.has(id)

    switch (id) {
      case 'editPhoto':
        return isPortraitSource
      case 'photoEffects':
      case 'tagPhoto':
        return hasPortraitMetadata
      case 'removeBackground':
        return isPortraitSource || isRemovingBackground
      case 'caption':
        return isCaptionDisabled
      default:
        return false
    }
  }

  return {
    photoIdentity,
    fileNameLabel,
    hasPortraitMetadata,
    isPortraitSource,
    isCaptionDisabled,
    portraitStudentName,
    taggedLines,
    dimensionsLabel,
    fileSize,
    albumLabel,
    uploadedByLabel,
    approvedByLabel,
    portraitLabel,
    isActionDisabled,
  }
}
